import {isFeatureEnabled} from 'feature-flags';
import {Menu} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  useGetMultiFactorAuthenticationSettingsQuery,
  useGetMyMultiFactorAuthenticationSettingsQuery,
} from '@dms/api/base';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';
import {NavigationItem, usePermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

export function MyProfileNavigation() {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {data: mfaSettings} = useGetMultiFactorAuthenticationSettingsQuery();
  const {data: userMfaSettings} = useGetMyMultiFactorAuthenticationSettingsQuery();
  const hasCurrentUserActiveMfa = isNotNil(userMfaSettings?.activatedAt);
  const isCurrentUserMfaDeactivated = isNotNil(userMfaSettings?.deactivatedAt);

  const [canReadMfa] = usePermissions({
    permissionKeys: ['canReadUserMultiFactorAuthenticationSettings'],
  });

  const menuItems = buildArray<NavigationItem>([
    {
      id: 'my-profile-personal-information',
      label: i18n.t('entity.user.labels.personalInformation'),
      href: myProfileRoutes.personalInformation,
      leftIcon: 'social/person',
    },
    {
      id: 'my-profile-security',
      label: i18n.t('entity.user.labels.security'),
      href: myProfileRoutes.security,
      leftIcon: 'hardware/security',
    },
    {
      id: 'my-profile-dashboard',
      label: i18n.t('page.myProfile.dashboard.sidebarTitle'),
      href: myProfileRoutes.dashboard,
      leftIcon: 'action/dashboard',
    },
    {
      id: 'my-profile-language',
      label: i18n.t('entity.user.labels.langAndTime'),
      href: myProfileRoutes.language,
      leftIcon: 'action/language',
    },
    {
      id: 'my-profile-notifications',
      label: i18n.t('page.settings.notifications.title'),
      href: myProfileRoutes.notifications,
      leftIcon: 'alert/notification_important',
    },
    {
      id: 'my-profile-email-signature',
      label: i18n.t('page.myProfile.labels.emailSignature'),
      href: myProfileRoutes.emailSignature,
      leftIcon: 'content/mail',
    },
    {
      id: 'my-profile-email-emailServices',
      label: i18n.t('page.myProfile.labels.emailServices'),
      href: myProfileRoutes.emailServices,
      leftIcon: 'content/mail',
    },
    {
      id: 'my-profile-external-systems',
      label: i18n.t('page.employeeDetail.labels.externalAuthorizationSystem'),
      href: myProfileRoutes.externalSystems,
      leftIcon: 'content/next_week',
    },
    {
      id: 'my-profile-table-exports',
      label: i18n.t('page.myProfile.labels.tableExports'),
      href: myProfileRoutes.tableExports,
      leftIcon: 'file/download',
    },
  ])
    .whenFeatureEnabled(featureFlags.MESSAGE_SYNCHRONIZATION, {
      id: 'my-profile-email-synchronization',
      label: i18n.t('page.myProfile.labels.emailSynchronization'),
      href: myProfileRoutes.emailSynchronization,
      leftIcon: 'action/cached',
    })
    .when(isFeatureEnabled(featureFlags.OUTLOOK_INTEGRATION), {
      id: 'my-profile-calendar-synchronization',
      label: i18n.t('page.myProfile.labels.calendarSynchronization'),
      href: myProfileRoutes.calendarSynchronization,
      leftIcon: 'action/calendar_today',
    })
    .when(
      !isCurrentUserMfaDeactivated &&
        canReadMfa &&
        mfaSettings?.active &&
        isFeatureEnabled(featureFlags.SETTINGS_TWO_FACTOR_AUTH),
      {
        id: 'my-profile-multi-factor-authentication',
        label: i18n.t('page.settings.labels.multiFactorAuth'),
        href: myProfileRoutes.multiFactorAuthentication,
        leftIcon: 'action/lock',
        badge: hasCurrentUserActiveMfa
          ? {colorScheme: 'green', icon: 'navigation/check'}
          : {colorScheme: 'red', icon: 'navigation/close'},
      }
    );

  const allItems = menuItems;
  const activeItem = allItems.find((item) => item.href && pathname.includes(item.href));

  const handleItemChange = (id: string) => {
    const item = allItems.find((item) => item.id === id);
    if (item?.href) {
      navigate(item.href);
    }
  };

  return (
    <Menu
      items={menuItems}
      activeItemId={activeItem?.id}
      onItemChange={handleItemChange}
      isSearchable
      data-testid="menu"
    />
  );
}
